import { useState } from 'react';
import type { Lead, LeadStatus } from '../../../../mocks/crm';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

interface Column {
  status: LeadStatus;
  color: string;
  bgColor: string;
  borderColor: string;
}

const columns: Column[] = [
  { status: 'New', color: '#0D9488', bgColor: '#F0FDFA', borderColor: '#99F6E4' },
  { status: 'Contacted', color: '#7C3AED', bgColor: '#F5F3FF', borderColor: '#DDD6FE' },
  { status: 'Quoted', color: '#D97706', bgColor: '#FFFBEB', borderColor: '#FDE68A' },
  { status: 'Negotiating', color: '#EA580C', bgColor: '#FFF7ED', borderColor: '#FED7AA' },
  { status: 'Won', color: '#059669', bgColor: '#ECFDF5', borderColor: '#A7F3D0' },
  { status: 'Lost', color: '#DC2626', bgColor: '#FEF2F2', borderColor: '#FECACA' },
];

interface LeadsKanbanProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
  onStatusChange: (leadId: string, newStatus: LeadStatus) => void;
}

const sourceIconMap: Record<string, string> = {
  WhatsApp: 'ri-whatsapp-line',
  Website: 'ri-global-line',
  Phone: 'ri-phone-line',
  Email: 'ri-mail-line',
  Referral: 'ri-user-heart-line',
  'Walk-in': 'ri-store-line',
};

export default function LeadsKanban({ leads, onLeadClick, onStatusChange }: LeadsKanbanProps) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<LeadStatus | null>(null);

  const getColumnLeads = (status: LeadStatus) =>
    leads.filter((l) => l.status === status);

  const getColumnTotal = (status: LeadStatus) =>
    getColumnLeads(status).reduce((sum, l) => sum + l.budgetNum, 0);

  const handleDrop = (targetStatus: LeadStatus) => {
    if (dragging && dragging !== '') {
      onStatusChange(dragging, targetStatus);
    }
    setDragging(null);
    setDragOver(null);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-3">
      {columns.map((col) => {
        const colLeads = getColumnLeads(col.status);
        const colTotal = getColumnTotal(col.status);
        const isOver = dragOver === col.status;

        return (
          <div
            key={col.status}
            className={`flex-shrink-0 w-60 rounded-2xl flex flex-col transition-all duration-200 ${
              isOver ? 'ring-2' : ''
            }`}
            style={{
              backgroundColor: col.bgColor,
              border: `1px solid ${col.borderColor}`,
              ...(isOver ? { outlineColor: col.color, outline: `2px solid ${col.color}` } : {}),
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.status); }}
            onDrop={() => handleDrop(col.status)}
            onDragLeave={() => setDragOver(null)}
          >
            {/* Column Header */}
            <div className="px-3 pt-3 pb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: col.color }}>
                  {col.status}
                </span>
                <span
                  className="text-xs font-bold px-1.5 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: col.color }}
                >
                  {colLeads.length}
                </span>
              </div>
              {colTotal > 0 && (
                <span className="text-xs font-semibold text-gray-500">
                  {(colTotal / 1000).toFixed(0)}K
                </span>
              )}
            </div>

            {/* Cards */}
            <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 max-h-[520px]">
              {colLeads.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => setDragging(lead.id)}
                  onDragEnd={() => setDragging(null)}
                  onClick={() => onLeadClick(lead)}
                  className={`bg-white rounded-xl p-3 cursor-pointer border border-gray-100 hover:border-gray-200 transition-all ${
                    dragging === lead.id ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-1 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 leading-snug truncate">{lead.name}</p>
                      <p className="text-xs text-gray-400 truncate">{lead.phone}</p>
                    </div>
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: brand }}
                    >
                      {lead.assignedAvatar}
                    </div>
                  </div>

                  <div
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium mb-2"
                    style={{ backgroundColor: `${col.color}15`, color: col.color }}
                  >
                    <i className={`${lead.serviceIcon} text-xs`} />
                    <span className="truncate max-w-[140px]">{lead.service}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-700">{lead.budget}</span>
                    <div className="flex items-center gap-1 text-gray-400">
                      <i className={`${sourceIconMap[lead.source] || 'ri-global-line'} text-xs`} />
                      <span className="text-xs">{lead.pax}pax</span>
                    </div>
                  </div>

                  {lead.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {lead.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add button */}
            <div className="px-2 pb-2">
              <button
                className="w-full text-xs font-medium py-2 rounded-xl border border-dashed border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 cursor-pointer transition-colors flex items-center justify-center gap-1"
              >
                <i className="ri-add-line" /> Add Lead
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
